import { Backdrop, CircularProgress } from "@mui/material";
import React, { createContext, useContext, useState } from "react";

type LoadSpinnerContextType = {
    start: () => void;
    stop: () => void;
};

const LoadSpinnerContext = createContext<LoadSpinnerContextType | null>(null);

/**
 * React component that provides a blocking full-screen load spinner overlay.
 * Supports nested start/stop calls — the overlay stays visible until every
 * start() has been matched by a stop().
 */
export function LoadSpinnerProvider({ children }: { children: React.ReactNode }) {
    const [loadingCount, setLoadingCount] = useState(0);

    const start = () => setLoadingCount((c) => c + 1);
    const stop = () => setLoadingCount((c) => Math.max(0, c - 1));

    return (
        <LoadSpinnerContext.Provider value={{ start, stop }}>
            {children}
            <Backdrop open={loadingCount > 0} style={{ zIndex: 9999, color: "#fff" }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </LoadSpinnerContext.Provider>
    );
}

/**
 * Hook for showing and hiding the blocking load spinner.
 * Usage: `const { start, stop } = useLoadSpinner();`
 * Throws if used outside a LoadSpinnerProvider.
 */
export function useLoadSpinner(): LoadSpinnerContextType {
    const ctx = useContext(LoadSpinnerContext);
    if (!ctx) throw new Error("useLoadSpinner must be used inside a LoadSpinnerProvider");
    return ctx;
}
